/**
 * The Inbox as one triage list (redesign spec, inbox.md 3 and 4.4): every kind of ask that needs
 * a person in one model, grouped and ordered by urgency. Pure and unit tested; the page reads it.
 *
 * Order: requests (the one order of decision.ts: soonest deadline, then risk, then age), then Claude's questions,
 * plans and MCP forms answered in place (R-HL-6, soonest deadline first), then blocked requests,
 * assistant steps and sessions waiting (each oldest wait first), then asked questions: a turn
 * that ended on a question with nothing held (ADR 0056), quiet and last. Done is the decided history.
 */
import type { ApprovalBlock, ApprovalRequest, AskSetAside, AssistantState, Session } from '@orgus/protocol';
import { sessionAskOf } from '@orgus/protocol';
import { baseName, middleEllipsis, shortPath } from '@orgus/ui';
import { askDoneLabel, askKindOf, isAnswerable, settledAt } from './askModel.ts';
import type { AskKind } from './askModel.ts';
import { ASK_GROUP_LABEL, compareAsks, sortRequests } from './decision.ts';
import type { AskGroup } from './decision.ts';

export type InboxKind = 'request' | 'ask' | 'blocked' | 'step' | 'question' | 'asked';
export type InboxScope = 'open' | 'expiring' | 'done';

type PendingStep = AssistantState['pending'][number];

export type InboxItem =
  | { kind: 'request'; key: string; at: number; approval: ApprovalRequest }
  /** A question, plan or MCP form a held hook waits on: answered in place (R-HL-6). */
  | { kind: 'ask'; ask: AskKind; key: string; at: number; approval: ApprovalRequest }
  | { kind: 'blocked'; key: string; at: number; approval: ApprovalRequest; block: ApprovalBlock }
  | { kind: 'step'; key: string; at: number; step: PendingStep }
  | { kind: 'question'; key: string; at: number; session: Session }
  /** `open`: asked over a day ago, listed but not counted (an open question). */
  | { kind: 'asked'; key: string; at: number; session: Session; open?: boolean }
  /** A dismissed or snoozed question, in Done for 7 days with Undo. */
  | { kind: 'dismissed'; key: string; at: number; record: AskSetAside; session: Session | null }
  | { kind: 'done'; key: string; at: number; approval: ApprovalRequest };

/** Each kind's group in the one vocabulary every surface uses (decision.ts, ADR 0072). */
export const GROUP_OF: Record<InboxKind, AskGroup> = { request: 'requests', ask: 'questions', blocked: 'blocked', step: 'assistant', question: 'sessions', asked: 'asked' };

export const GROUP_LABEL: Record<InboxKind, string> = {
  request: ASK_GROUP_LABEL.requests,
  ask: ASK_GROUP_LABEL.questions,
  blocked: ASK_GROUP_LABEL.blocked,
  step: ASK_GROUP_LABEL.assistant,
  question: ASK_GROUP_LABEL.sessions,
  asked: ASK_GROUP_LABEL.asked,
};

export const GROUP_ORDER: readonly InboxKind[] = ['request', 'ask', 'blocked', 'step', 'question', 'asked'];

/** A request this close to its deadline counts as expiring soon. */
export const EXPIRING_MS = 3 * 60_000;

export const itemKey = {
  request: (id: string) => `r:${id}`,
  ask: (id: string) => `a:${id}`,
  blocked: (id: string) => `b:${id}`,
  step: (id: string) => `s:${id}`,
  question: (id: string) => `q:${id}`,
  asked: (id: string) => `k:${id}`,
  dismissed: (askId: string) => `x:${askId}`,
  done: (id: string) => `d:${id}`,
};

export interface InboxSource {
  pending: readonly ApprovalRequest[];
  blocked: readonly { approval: ApprovalRequest; block: ApprovalBlock }[];
  steps: readonly PendingStep[];
  /** Sessions waiting on a person that no pending request covers, blocked (the needsYou selector). */
  questions: readonly Session[];
  /** Sessions whose turn ended on a question, nothing held, under a day old and not set aside. */
  asked?: readonly Session[];
  /** Asked questions over a day old: listed after the fresh ones, not counted. */
  open?: readonly Session[];
}

/** Every open item, grouped by kind in the urgency order, each group in its own order. */
export function inboxGroups(src: InboxSource): { kind: InboxKind; items: InboxItem[] }[] {
  const pending = sortRequests(src.pending.filter((a) => a.state === 'pending'));
  const requests: InboxItem[] = pending.filter((a) => !isAnswerable(a)).map((a) => ({ kind: 'request', key: itemKey.request(a.id), at: a.createdAt, approval: a }));
  const asks: InboxItem[] = pending.filter(isAnswerable).map((a) => ({ kind: 'ask', ask: askKindOf(a), key: itemKey.ask(a.id), at: a.createdAt, approval: a }));
  const blocked: InboxItem[] = src.blocked
    .map(({ approval, block }) => ({ kind: 'blocked' as const, key: itemKey.blocked(approval.id), at: approval.decidedAt ?? approval.createdAt, approval, block }))
    .sort((x, y) => x.at - y.at);
  const steps: InboxItem[] = src.steps.map((s) => ({ kind: 'step' as const, key: itemKey.step(s.step.approval?.id ?? s.entryId), at: s.at, step: s })).sort((x, y) => x.at - y.at);
  const questions: InboxItem[] = src.questions.map((s) => ({ kind: 'question' as const, key: itemKey.question(s.id), at: s.lastActivity, session: s })).sort((x, y) => x.at - y.at);
  const asked: InboxItem[] = [
    ...(src.asked ?? []).map((s) => ({ kind: 'asked' as const, key: itemKey.asked(s.id), at: askedAt(s), session: s })).sort((x, y) => x.at - y.at),
    ...(src.open ?? []).map((s) => ({ kind: 'asked' as const, key: itemKey.asked(s.id), at: askedAt(s), session: s, open: true })).sort((x, y) => y.at - x.at),
  ];
  const by: Record<InboxKind, InboxItem[]> = { request: requests, ask: asks, blocked, step: steps, question: questions, asked };
  return GROUP_ORDER.filter((k) => by[k].length > 0).map((kind) => ({ kind, items: by[kind] }));
}

/** When a session asked: its SessionAsk's time, else its last activity. */
function askedAt(s: Session): number {
  return sessionAskOf(s.ext)?.askedAt || s.lastActivity;
}

/** Requests, questions, plans and forms whose deadline is within EXPIRING_MS. */
export function expiringItems(groups: ReturnType<typeof inboxGroups>, now: number): InboxItem[] {
  return groups.flatMap((g) => g.items).filter((i) => (i.kind === 'request' || i.kind === 'ask') && i.approval.expiresAt - now < EXPIRING_MS);
}

/** The decided history, newest first, as items. */
export function doneItems(recent: readonly ApprovalRequest[]): InboxItem[] {
  return recent
    .filter((a) => a.state !== 'pending')
    .map((a) => ({ kind: 'done' as const, key: itemKey.done(a.id), at: settledAt(a), approval: a }))
    .sort((x, y) => y.at - x.at);
}

/** Dismissed and snoozed questions, newest first, for Done (kept 7 days by the hub). */
export function dismissedItems(setAside: readonly AskSetAside[], sessions: Readonly<Record<string, Session>>): InboxItem[] {
  return setAside
    .map((record) => ({ kind: 'dismissed' as const, key: itemKey.dismissed(record.askId), at: record.at, record, session: sessions[record.sessionId] ?? null }))
    .sort((x, y) => y.at - x.at);
}

/** Open questions (asked over a day ago) that a bulk dismissal takes: never anything blocked. */
export function openAsked(groups: ReturnType<typeof inboxGroups>): Extract<InboxItem, { kind: 'asked' }>[] {
  return groups.flatMap((g) => g.items).filter((i): i is Extract<InboxItem, { kind: 'asked' }> => i.kind === 'asked' && !!i.open);
}

/** How many decisions were made since the start of today (local time). */
export function doneToday(recent: readonly ApprovalRequest[], now: number): number {
  const d = new Date(now);
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return recent.filter((a) => a.state !== 'pending' && (a.decidedAt ?? a.createdAt) >= start).length;
}

/** The oldest wait among open items, or null. */
export function oldestWait(groups: ReturnType<typeof inboxGroups>): number | null {
  return groups.flatMap((g) => g.items).reduce<number | null>((m, i) => (m == null || i.at < m ? i.at : m), null);
}

/** The key the deep link `?approval=<id>` points at: the pending request, else the blocked one, else a decided one. */
export function keyForApproval(id: string, groups: ReturnType<typeof inboxGroups>, done: readonly InboxItem[]): string | null {
  for (const k of [itemKey.request(id), itemKey.ask(id), itemKey.blocked(id), itemKey.step(id)]) if (groups.some((g) => g.items.some((i) => i.key === k))) return k;
  const d = itemKey.done(id);
  return done.some((i) => i.key === d) ? d : null;
}

/**
 * A section lists this many rows before the rest fold behind "Show N more". Every row of a
 * 387-question Asked section was 10,000 elements and froze the page as it opened (21 Oct 2026).
 */
export const SECTION_ROWS = 50;

/** The rows a section shows: the first `limit`, or down to the open one when the keys moved past them. */
export function shownRows<T extends { key: string }>(items: readonly T[], selected: string | null, all: boolean, limit = SECTION_ROWS): readonly T[] {
  if (all || items.length <= limit) return items;
  const i = selected ? items.findIndex((x) => x.key === selected) : -1;
  return items.slice(0, Math.max(limit, i + 1));
}

/** The next selection after an item leaves: the one after it, else the one before. */
export function nextAfter(list: readonly InboxItem[], key: string): string | null {
  const i = list.findIndex((x) => x.key === key);
  if (i < 0) return list[0]?.key ?? null;
  return list[i + 1]?.key ?? list[i - 1]?.key ?? null;
}

/** "Bash" to the glyph family of the call. */
export type ToolFamily = 'shell' | 'edit' | 'web' | 'mcp' | 'other';
export function toolFamily(tool: string | undefined): ToolFamily {
  const t = (tool ?? '').toLowerCase();
  if (t === 'bash' || t === 'powershell') return 'shell';
  if (t === 'edit' || t === 'multiedit' || t === 'write' || t === 'notebookedit') return 'edit';
  if (t === 'webfetch' || t === 'websearch') return 'web';
  if (t.startsWith('mcp')) return 'mcp';
  return 'other';
}

/**
 * A request's title as its verb and its target: "Edit" and the file, "Run" and the command,
 * "Fetch" and the URL. Rows and the panel show the verb as words and the target small in mono,
 * cut from the middle, with the whole target in a tooltip. Titles without a target stay whole.
 */
export interface TitleParts {
  verb: string;
  /** What it acts on, as the title shows it (a path relative to the session's folder when inside it). */
  target: string;
  /** The whole target for the tooltip: the absolute path when the input has one. */
  full: string;
  kind: 'path' | 'command' | 'url' | 'text';
}

const TITLE_VERB = /^(Edit notebook|Edit|Write|Read|Run|Fetch|Search the web for)\s+([\s\S]+)$/;

export function titleParts(a: Pick<ApprovalRequest, 'title' | 'input'>): TitleParts {
  const m = TITLE_VERB.exec(a.title.trim());
  if (!m) return { verb: a.title, target: '', full: '', kind: 'text' };
  const verb = m[1]!;
  const target = m[2]!.trim();
  const kind: TitleParts['kind'] = verb === 'Run' ? 'command' : verb === 'Fetch' ? 'url' : verb.startsWith('Search') ? 'text' : 'path';
  const input = a.input && typeof a.input === 'object' ? (a.input as Record<string, unknown>) : {};
  const file = typeof input.file_path === 'string' ? input.file_path : typeof input.notebook_path === 'string' ? input.notebook_path : undefined;
  const full = kind === 'path' && file ? file : target;
  return { verb: verb.startsWith('Search') ? 'Search' : verb, target, full, kind };
}

/** The target at most `max` characters: a path keeps its last segments, a URL both ends, a command its start. */
export function shortTarget(p: TitleParts, max = 44): string {
  if (p.target.length <= max) return p.target;
  if (p.kind === 'path') {
    const s = shortPath(p.target, max);
    return s.length <= max ? s : middleEllipsis(s, max);
  }
  if (p.kind === 'command') return `${p.target.slice(0, max - 1).trimEnd()}…`;
  return middleEllipsis(p.target, max);
}

/** A short line for a toast: "Edit tsconfig.base.json", "Run pnpm add zod@^4.1.0". */
export function shortTitle(a: Pick<ApprovalRequest, 'title' | 'input'>, max = 28): string {
  const p = titleParts(a);
  if (!p.target) return middleEllipsis(p.verb, max + 8);
  const target = p.kind === 'path' ? baseName(p.target) : p.target;
  return `${p.verb} ${shortTarget({ ...p, target }, max)}`;
}

/** What stopped a blocked request, short: "denied by srikanth", "expired", "refused by policy". */
export function blockedCause(a: Pick<ApprovalRequest, 'state' | 'decidedBy'>): string {
  if (a.state === 'expired') return a.decidedBy === 'machine' ? 'machine lost contact' : 'expired with no answer';
  if (a.decidedBy === 'policy') return 'refused by policy';
  return `denied by ${a.decidedBy ?? 'someone'}`;
}

/** The decided label for a Done row. */
export function doneLabel(a: Pick<ApprovalRequest, 'state' | 'scope'> & Partial<Pick<ApprovalRequest, 'kind' | 'ask' | 'fallback' | 'handedBack' | 'sessionAnswer'>>): string {
  if (a.kind && isAnswerable({ kind: a.kind, ask: a.ask })) return askDoneLabel({ kind: a.kind, state: a.state, fallback: a.fallback, handedBack: a.handedBack, sessionAnswer: a.sessionAnswer });
  if (a.state === 'allowed') return a.scope === 'session' ? 'Allowed for session' : a.scope === 'project' ? 'Allowed by rule' : 'Allowed once';
  if (a.state === 'denied') return 'Denied';
  return 'Expired';
}

// ---------------------------------------------------------------------------
// Grouping and bulk (ADR 0072)
// ---------------------------------------------------------------------------

/** How the open list is grouped: by kind (the default), by the session that asks, or by tool. */
export type InboxGrouping = 'kind' | 'session' | 'tool';

/** One section of the open list: a kind's group, or (by session or tool) the pending requests and questions of one session or one tool. */
export interface InboxSection {
  key: string;
  kind: InboxKind;
  /** By session: the session. By tool: the tool group's label ("Bash pnpm", "Read"). */
  sessionId?: string;
  tool?: string;
  items: InboxItem[];
}

const pendingOf = (i: InboxItem): ApprovalRequest | null => (i.kind === 'request' || i.kind === 'ask' ? i.approval : null);

/** A tool request's group by tool: the tool, and for a shell its program ("Bash pnpm"); a question, plan or form keeps its kind. */
export function toolGroupOf(a: Pick<ApprovalRequest, 'tool' | 'input'>): string {
  const tool = a.tool ?? 'Other';
  if (toolFamily(tool) !== 'shell') return tool;
  const cmd = a.input && typeof a.input === 'object' ? (a.input as Record<string, unknown>).command : undefined;
  const head = typeof cmd === 'string' ? cmd.trim().split(/\s+/)[0] : '';
  return head ? `${tool} ${baseName(head)}` : tool;
}

/** The distinct tools of a section, in order: "Read, Grep, Bash". */
export function toolsOf(items: readonly InboxItem[]): string[] {
  const out: string[] = [];
  for (const i of items) {
    const a = pendingOf(i);
    const t = a ? (isAnswerable(a) ? askKindOf(a) === 'plan' ? 'Plan' : askKindOf(a) === 'elicitation' ? 'Form' : 'Question' : (a.tool ?? 'Other')) : null;
    if (t && !out.includes(t)) out.push(t);
  }
  return out;
}

/**
 * The open list's sections. By kind: one per group. By session or by tool: the pending requests
 * and questions regrouped (each section in the one order, sections by their most urgent item),
 * then the rest by kind as usual (blocked, assistant steps, sessions waiting, asked).
 */
export function inboxSections(groups: ReturnType<typeof inboxGroups>, by: InboxGrouping): InboxSection[] {
  const byKind = groups.map((g) => ({ key: g.kind, kind: g.kind, items: g.items }));
  if (by === 'kind') return byKind;
  const pending = groups.filter((g) => g.kind === 'request' || g.kind === 'ask').flatMap((g) => g.items);
  const rest = byKind.filter((s) => s.kind !== 'request' && s.kind !== 'ask');
  const order = (i: InboxItem) => {
    const a = pendingOf(i)!;
    return { deadline: a.expiresAt, risk: a.risk, at: a.createdAt };
  };
  const sorted = [...pending].sort((x, y) => compareAsks(order(x), order(y)));
  const sections = new Map<string, InboxSection>();
  for (const i of sorted) {
    const a = pendingOf(i)!;
    const asks = i.kind === 'ask';
    const key = by === 'session' ? `s:${a.sessionId}` : asks ? 'k:ask' : `t:${toolGroupOf(a)}`;
    const sec = sections.get(key) ?? { key, kind: by === 'tool' && asks ? 'ask' : 'request', ...(by === 'session' ? { sessionId: a.sessionId } : asks ? {} : { tool: toolGroupOf(a) }), items: [] };
    sec.items.push(i);
    sections.set(key, sec);
  }
  return [...sections.values(), ...rest];
}

/** What a bulk action may take from a selection: pending requests and questions, and asked questions for Dismiss. Never anything blocked (ADR 0056). */
export const isSelectable = (i: InboxItem): boolean => i.kind === 'request' || i.kind === 'ask' || i.kind === 'asked';
